import {Component, EventEmitter, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from "@angular/common";
import {Session} from "../../services/Session";
import {takeUntil} from "rxjs/operators";
import {TOAST_TYPE} from "../../constants/Tpiq.constants";
import {ActivatedRoute, Router} from "@angular/router";
import {SwitchBoard} from "../../services/SwitchBoard";
import {StateService} from "../../services/StateService";
import {PageTitleService} from "../../services/PageTitle";
import {ToastService} from "../../services/Toast.service";
import {LoadingService} from "../../services/LoadingService";
import {TaxPlanningService} from "../../tpiq-services/tax-planning.service";
import {QuestionnairesSurveysService} from "../../services/questionnaires-surveys.service";
import {OrganizersService} from "../../tpiq-services/organizers.service";
import {MatDialog} from "@angular/material/dialog";
import {FormsModule} from "@angular/forms";
import {ClientPlansComponent} from "../client-plans/client-plans.component";
import {QsSecurelinkDialogComponent} from "../../questionnaires/qs-securelink-dialog/qs-securelink-dialog.component";
import {CreateOrUpdateClientComponent} from "../clients/create-or-update-client/create-or-update-client.component";
// ClickUp 868jtq1h0: TaxProjectionsListComponent + FeatureFlagService imports
// removed. The legacy Tax Projections tab was the only consumer in this
// component; with the tab gone there's no `enableTaxProjections` gating to do.
// Tax Projections lives on the new platform reached via /choose-platform.
import {FoundationInit} from "../../directives/foundation.directive";

declare const _: any;

@Component({
  selector: 'app-client-info',
  standalone: true,
  imports: [CommonModule, FormsModule, ClientPlansComponent, QsSecurelinkDialogComponent, CreateOrUpdateClientComponent,
  FoundationInit],
  templateUrl: './client-info.component.html',
  styleUrl: './client-info.component.scss'
})
export class ClientInfoComponent implements OnInit, OnDestroy {
  org: string;
  user: any;
  reveal: any = {
    editClient: false
  };
  clientsList: Array<any> = [];
  thirdPartiesList: Array<any> = [];
  clientUserId: string;
  client: any;
  destroy$: EventEmitter<void> = new EventEmitter<void>();
  clientPortalAccess: boolean;
  protectedView = false;
  questionnaire: any = {};
  // Post-send tracking status from the organizer service ('Sent', 'In Progress', 'Submitted', 'Locked', 'Done').
  // Drives the Submitted state + View Answers link; the questionnaires/customer endpoint only knows sent-or-not.
  questionnaireTrackStatus = '';
  questionnaireRow: any = null;
  estimatesAccess: boolean;
  presentationAccess: boolean;
  organizerAccess: boolean;
  // ClickUp 868jtq1h0: projectionsAccess + currentTab removed alongside the
  // legacy Tax Projections tab. The non-tabbed view is now the only render
  // path for client-info.
  currentPlan: string;
  userReadOnly: any;

  constructor(
    private _router: Router,
    private dialog: MatDialog,
    private _route: ActivatedRoute,
    private switchBoard: SwitchBoard,
    private stateService: StateService,
    private titleService: PageTitleService,
    private toastService: ToastService,
    private loadingService: LoadingService,
    private taxPlanningService: TaxPlanningService,
    private qsService: QuestionnairesSurveysService,
    private organizersService: OrganizersService,
  ) {}

  ngOnInit(): void {
    this.titleService.setPageTitle('');
    this.user = Session.getUser();
    this.org = Session.getOrg();
    const accessInfo = Session.getUserAccessInfo();
    this.clientPortalAccess = (accessInfo && accessInfo.clientPortalAccess) ? true : false;
    if (Session.getTaxPlanningProtectedView()) {
      this.protectedView = true;
    } else {
      this.protectedView = false;
      Session.setTaxPlanningProtectedView(this.protectedView);
    }

    this._route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
    });

    this._route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.clientUserId = params.clientId;
      this.getSubscriptionInfo();
      if (this.clientUserId){
        this.getClient();
      }
    });
    this.switchBoard.onPageChangeListener.next({
      url: this._route.url,
      name: 'Client',
      id: 'CLIENT'
    });
    this.switchBoard.onClickPrev.pipe(takeUntil(this.destroy$)).subscribe(obj => {
      this.handleBackButton();
    });
  }

  getSubscriptionInfo() {
    this.taxPlanningService.getSubscriptionStatus(this.org, this.user.id).subscribe(subscription => {
      this.currentPlan = subscription.subscriptionStatus;
      this.userReadOnly = (this.currentPlan.includes("Pay-Per-Tax-Plan") && this.taxPlanningService.getUserReadOnly()) ? true : false;
      if (subscription.accessList && subscription.accessList.length > 0) {
        this.estimatesAccess = subscription.accessList.indexOf('Estimates') != -1;
        this.presentationAccess = subscription.accessList.indexOf('Presentation') != -1;
        this.organizerAccess = subscription.accessList.indexOf('Organizer') != -1;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  getClient(){
    this.taxPlanningService.getClient(Session.getOrg(), this.user.id, this.clientUserId, this.protectedView)
      .subscribe(response => {
        let data = _.cloneDeep(response);
        this.formatClientData(response);
      });
  }

  formatClientData(data) {
    this.client = data || {};
    this.clientsList = data.relatedParties || [];
    this.thirdPartiesList = data.thirdParties || [];
    setTimeout(() => {
      this.getQuestionnaireDetails();
    }, 500);
  }

  handleBackButton(){
    const prevState = this.stateService.getPrevState();
    if (prevState){
      this.stateService.pop();
      this._router.navigate([prevState.url]);
    }else {
      this._router.navigate(['tax-planning/clients']);
    }
  }

  getQuestionnaireDetails(){
    if (this.organizerAccess){
      this.loadingService.triggerLoadingEvent(true);
      // The component instance is reused across client navigations — reset the
      // previous client's questionnaire state so a stale status/"View Answers"
      // target never shows while the new fetch is in flight.
      this.questionnaire = {};
      this.questionnaireTrackStatus = '';
      this.questionnaireRow = null;
      // The service caches details per user for the whole session; clear so a
      // questionnaire submitted since the last visit is reflected here.
      this.qsService.clearQuestionnaireData();
      this.qsService.getQuestionnaireDetails(this.org, this.client.email).subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        this.questionnaire = response;
        if (this.questionnaire.resend && this.questionnaire.questionnaireId) {
          this.getQuestionnaireTrackStatus();
        }
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
      });
    }
  }

  getQuestionnaireTrackStatus(){
    const filters = {
      questionnaireId: this.questionnaire.questionnaireId,
      clientIds: [this.client.id],
      fromIndex: 0
    };
    this.organizersService.getOrganizersCount(this.user.id, this.org, filters).subscribe(response => {
      if (this.client.id !== filters.clientIds[0]) {
        return; // late response for a previously viewed client
      }
      // Response is per-status counts for this client; pick the furthest stage reached.
      const stages = ['Done', 'Locked', 'Submitted', 'In Progress', 'Sent'];
      const current = _.find(stages, (stage) => {
        const entry = _.find(response, {value: stage});
        return entry && entry.count > 0;
      });
      this.questionnaireTrackStatus = current || '';
      if (current && current !== 'Sent') {
        this.getQuestionnaireRow(current, filters);
      }
    }, error => {
    });
  }

  getQuestionnaireRow(status, filters){
    this.organizersService.getPaginatedOrganizersList(this.user.id, this.org, {...filters, status}).subscribe(response => {
      if (this.client.id !== filters.clientIds[0]) {
        return; // late response for a previously viewed client
      }
      const rows = (response && response.questionnaires) || [];
      this.questionnaireRow = _.find(rows, {clientId: this.client.id}) || rows[0] || null;
    }, error => {
    });
  }

  viewAnswers(){
    if (!this.questionnaireRow) {
      return;
    }
    // Same route the organizer list uses; it strips the 'TPIQ ' prefix from the name param.
    let name = this.questionnaireRow.questionnaireName || '';
    if (name.startsWith('TPIQ ')) {
      name = name.substring(5);
    }
    this._router.navigate(['taxplanningClients', this.client.id, 'questionnaire-response', this.questionnaireRow.questionnaireId, name]);
  }

  addQuestionnaire(){
    const dialogRef = this.dialog.open(QsSecurelinkDialogComponent, {
      data: {
        firstName: this.client.firstName,
        lastName: this.client.lastName,
        email: this.client.email,
        clientId: this.client.id,
        questionnaireMetadata: this.questionnaire,
        questionnaireName: "TPIQ Standard Questionnaire",
        resend: this.questionnaire.resend,
        portalEnabled: this.client.enableClientPortal
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      console.log(`Dialog result: ${result}`);
      this.getQuestionnaireDetails();
    });
  }

  changeClientStatus() {
    const data: any = [];
    data.push(this.clientUserId);
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.makeClientInactive(this.org, this.user.id, data)
      .subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        this.taxPlanningService.clearClientData();
        this.getClient();
        if (response && response.message) {
          this.toastService.pop(TOAST_TYPE.success, response.message);
        } else {
          this.toastService.pop(TOAST_TYPE.success, 'Client status updated successfully');
        }
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        try {
          const message = JSON.parse(error).message || 'Failed to update client status';
          this.toastService.pop(TOAST_TYPE.error, message);
        } catch (err) {
          this.toastService.pop(TOAST_TYPE.error, 'Failed to update client status');
        }
      });
  }

  editClientInfo() {
    this.reveal.editClient = true;
  }

  refreshClient() {
    this.reveal.editClient = false;
    this.getClient();
  }

  hideReveal(key, overrideMinimizeStatus?){
    this.reveal[key] = false;
  }

}
